// Sprint 6 — Tab "Test sessions": lista test session recenti per il flow,
// con resume read-only o eliminazione.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { FlowWithRelations } from '@audiq/shared';
import { IconButton } from '@audiq/ui';
import { Eye, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { TestSessionDetail, TestSessionRow } from '../../lib/api/flows';
import { TestModeModal } from './TestModeModal';

dayjs.extend(relativeTime);
dayjs.locale('it');

interface Props {
  flow: FlowWithRelations;
}

export function TestSessionsTab({ flow }: Props) {
  const qc = useQueryClient();
  const [openSession, setOpenSession] = useState<string | null>(null);
  const [showNewTest, setShowNewTest] = useState(false);

  const sessionsQuery = useQuery({
    queryKey: ['test-sessions', flow.id],
    queryFn: () => api.listTestSessions(flow.id),
  });

  const deleteMut = useMutation({
    mutationFn: (sid: string) => api.deleteTestSession(flow.id, sid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['test-sessions', flow.id] }),
  });

  if (sessionsQuery.isLoading) return <p style={{ color: 'var(--mute)' }}>Carico…</p>;
  const rows = sessionsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'var(--mute)' }}>
          Le test session sono isolate dai dati di produzione e non contano per i consumi.
        </p>
        <button
          type="button"
          onClick={() => setShowNewTest(true)}
          className="rounded-md px-3 py-1.5 text-sm"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          ▶ Nuova test session
        </button>
      </div>

      {rows.length === 0 ? (
        <div
          className="rounded-md border p-6 text-center"
          style={{ borderColor: 'var(--line)', background: 'var(--paper)', color: 'var(--mute)' }}
        >
          Nessuna test session ancora. Avviane una per provare il flow in modalità chat.
        </div>
      ) : (
        <table
          className="w-full"
          style={{ borderCollapse: 'collapse', background: 'var(--paper)' }}
        >
          <thead>
            <tr className="mono" style={{ color: 'var(--mute)', textAlign: 'start', fontSize: 11 }}>
              <th className="border-b p-2 text-start" style={{ borderColor: 'var(--line)' }}>
                Avviata
              </th>
              <th className="border-b p-2 text-start" style={{ borderColor: 'var(--line)' }}>
                Stato
              </th>
              <th className="border-b p-2 text-start" style={{ borderColor: 'var(--line)' }}>
                Lingua
              </th>
              <th className="border-b p-2 text-start" style={{ borderColor: 'var(--line)' }}>
                Turni
              </th>
              <th className="border-b p-2 text-start" style={{ borderColor: 'var(--line)' }}>
                Completezza
              </th>
              <th className="border-b p-2" style={{ borderColor: 'var(--line)' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <SessionRow
                key={r.id}
                row={r}
                onOpen={() => setOpenSession(r.id)}
                onDelete={() => {
                  if (confirm('Eliminare questa test session?')) {
                    deleteMut.mutate(r.id);
                  }
                }}
              />
            ))}
          </tbody>
        </table>
      )}

      {openSession && (
        <SessionDetailModal
          flowId={flow.id}
          sessionId={openSession}
          onClose={() => setOpenSession(null)}
        />
      )}
      {showNewTest && <TestModeModal flow={flow} onClose={() => setShowNewTest(false)} />}
    </div>
  );
}

function SessionRow({
  row,
  onOpen,
  onDelete,
}: {
  row: TestSessionRow;
  onOpen: () => void;
  onDelete: () => void;
}) {
  return (
    <tr style={{ background: 'var(--paper)' }}>
      <td className="border-b p-2" style={{ borderColor: 'var(--line)', fontSize: 13 }}>
        {dayjs(row.started_at).format('DD MMM YYYY HH:mm:ss')}
      </td>
      <td className="border-b p-2" style={{ borderColor: 'var(--line)' }}>
        <span
          className="mono rounded-full px-2 py-0.5"
          style={{
            background:
              row.status === 'completed'
                ? 'color-mix(in oklab, var(--primary) 18%, transparent)'
                : row.status === 'aborted_emergency'
                  ? 'color-mix(in oklab, var(--danger) 18%, transparent)'
                  : 'var(--soft)',
            color:
              row.status === 'completed'
                ? 'var(--primary-2)'
                : row.status === 'aborted_emergency'
                  ? 'var(--danger)'
                  : 'var(--ink)',
            fontSize: 11,
          }}
        >
          {row.status}
        </span>
      </td>
      <td
        className="border-b p-2 mono"
        style={{ borderColor: 'var(--line)', fontSize: 12, color: 'var(--mute)' }}
      >
        {row.language_detected ?? '—'}
      </td>
      <td className="border-b p-2" style={{ borderColor: 'var(--line)', fontSize: 13 }}>
        {row.total_turns}
      </td>
      <td className="border-b p-2" style={{ borderColor: 'var(--line)', fontSize: 13 }}>
        {row.completeness_pct}%
      </td>
      <td className="border-b p-2 text-end" style={{ borderColor: 'var(--line)' }}>
        <div className="flex items-center justify-end gap-1">
          <IconButton icon={Eye} label="Apri" onClick={onOpen} />
          <IconButton icon={Trash2} label="Elimina" tone="danger" onClick={onDelete} />
        </div>
      </td>
    </tr>
  );
}

function SessionDetailModal({
  flowId,
  sessionId,
  onClose,
}: {
  flowId: string;
  sessionId: string;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['test-session-detail', flowId, sessionId],
    queryFn: () => api.getTestSession(flowId, sessionId),
  });
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(31,36,33,0.45)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--paper)',
          borderRadius: 12,
          width: 'min(680px, 100%)',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: 24,
          boxShadow: '0 8px 32px rgba(31,36,33,0.18)',
        }}
      >
        <div className="flex items-baseline justify-between">
          <h3 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
            Test session
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl"
            style={{ color: 'var(--mute)', lineHeight: 1 }}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        {detailQuery.isLoading && <p style={{ color: 'var(--mute)' }}>Carico…</p>}
        {detailQuery.data && <SessionDetailBody detail={detailQuery.data} />}
      </div>
    </div>
  );
}

function SessionDetailBody({ detail }: { detail: TestSessionDetail }) {
  return (
    <div className="mt-4 space-y-3">
      <div className="mono text-sm" style={{ color: 'var(--mute)' }}>
        Stato: <strong style={{ color: 'var(--ink)' }}>{detail.session.status}</strong> · Turni:{' '}
        {detail.session.total_turns} · Completezza: {detail.session.completeness_pct}%
        {detail.session.language_detected && <> · Lingua: {detail.session.language_detected}</>}
      </div>
      <div className="space-y-2">
        {detail.messages.map((m, i) => (
          <div
            key={i}
            style={{
              background:
                m.role === 'user'
                  ? 'color-mix(in oklab, var(--primary) 12%, transparent)'
                  : 'var(--soft)',
              borderRadius: 10,
              padding: '8px 12px',
              fontSize: 14,
              color: 'var(--ink)',
            }}
          >
            <div className="mono" style={{ fontSize: 10, color: 'var(--mute)', marginBottom: 2 }}>
              {m.role} · turno {m.turn}
            </div>
            {m.content}
          </div>
        ))}
      </div>
    </div>
  );
}
